import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Crown, Play, FileText, Plus, Trash2, Send, Bot, X } from 'lucide-react';
import { PageTitle } from '../components/PageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Knowledge = () => {
  const { user } = useAuth();
  const { theme, themes } = useTheme();
  const currentTheme = themes[theme] || themes.electric;
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [showConsultingForm, setShowConsultingForm] = useState(false);
  const [consultingStep, setConsultingStep] = useState(1);
  const [investorProfile, setInvestorProfile] = useState(null);
  const [showInvestorBot, setShowInvestorBot] = useState(false);
  const [botMessages, setBotMessages] = useState([]);
  const [botInput, setBotInput] = useState('');
  const [loadingBot, setLoadingBot] = useState(false);
  const chatEndRef = useRef(null);
  const [consultingData, setConsultingData] = useState({
    budget: '',
    purpose: '',
    location_preference: '',
    property_type_preference: '',
    timeline: '',
    investment_experience: '',
    risk_tolerance: '',
    financing_needed: '',
    expected_return: '',
    additional_requirements: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: ''
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      let imageUrl = '';
      if (imageFile) {
        const imgFormData = new FormData();
        imgFormData.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/admin/upload-image`, imgFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadRes.data.url;
      }

      await axios.post(`${API}/admin/blog`, {
        ...formData,
        image: imageUrl,
        author: 'Marcos Guisado, CEO de IaConsultora',
        tags: []
      });

      toast.success('Blog publicado correctamente');
      setShowNewPost(false);
      setFormData({ title: '', excerpt: '', content: '' });
      setImageFile(null);
      loadPosts();
    } catch (error) {
      toast.error('Error al crear blog');
    }
  };

  const handleDeletePost = async (postId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta entrada del blog?')) return;
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      toast.success('Entrada eliminada');
      loadPosts();
    } catch (error) {
      toast.error('Error al eliminar la entrada');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8 lg:p-12">
      <div className="max-w-6xl mx-auto pt-8">
        <PageTitle>Conocimiento</PageTitle>
        <p className="text-gray-400 text-sm mb-6">Aprende todo sobre el mercado inmobiliario</p>
        
        {/* Consejos Personalizados Section */}
        <div className="mb-10 rounded-2xl p-6" style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>
            <Crown style={{ color: '#c9a84c' }} size={22} />
            Consejos Personalizados
          </h3>
          <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'var(--font-body)' }}>
            Responde unas preguntas sobre tu situación y recibe consejos personalizados sobre inversiones inmobiliarias
          </p>
          <button onClick={() => setShowConsultingForm(true)}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 transition-opacity hover:opacity-80"
            style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)', fontFamily: 'var(--font-body)' }}
          >
            <FileText size={16} />
            Obtener Consejos Ahora
          </button>
        </div>
        
        {user?.is_admin && (
          <button
            onClick={() => setShowNewPost(!showNewPost)}
            className="bg-yellow-500 text-black px-6 py-3 font-bold uppercase flex items-center gap-2 border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition mb-8"
          >
            <Plus size={20} />
            Nueva Entrada
          </button>
        )}

        {showNewPost && user?.is_admin && (
          <div className="bg-gray-900 border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Crear Nuevo Blog</h2>
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div>
                <label className="block text-white font-bold mb-2">Título</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  placeholder="Ej: Guía completa para invertir en inmuebles"
                />
              </div>
              <div>
                <label className="block text-white font-bold mb-2">Extracto</label>
                <input
                  type="text"
                  required
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  placeholder="Resumen breve del artículo"
                />
              </div>
              <div>
                <label className="block text-white font-bold mb-2">Contenido</label>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full p-3 border-4 border-black"
                  rows="8"
                  placeholder="Contenido completo del blog..."
                ></textarea>
              </div>
              <div>
                <label className="block text-white font-bold mb-2">Imagen Principal</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  className="w-full p-3 border-4 border-black bg-white"
                />
                {imageFile && (
                  <p className="text-sm text-green-400 mt-2">✓ {imageFile.name}</p>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 text-black p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                >
                  Publicar
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewPost(false)}
                  className="flex-1 bg-gray-200 text-black p-3 font-bold border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {posts.map(post => (
            <div
              key={post.post_id}
              onClick={() => navigate(`/knowledge/${post.post_id}`)}
              className="bg-gray-900 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-all group cursor-pointer relative"
              data-testid={`blog-post-${post.post_id}`}
            >
              {user?.is_admin && (
                <button
                  onClick={(e) => handleDeletePost(post.post_id, e)}
                  className="absolute top-2 right-2 z-10 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Eliminar entrada"
                >
                  <Trash2 size={14} />
                </button>
              )}
              <div className="h-44 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-5">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {post.tags.map((tag, i) => (
                    <span key={i} className="text-[10px] font-medium text-gray-400 bg-gray-800 px-2 py-0.5 rounded">{tag}</span>
                  ))}
                </div>
                <h2 className="text-base font-semibold text-white mb-2 line-clamp-2">{post.title}</h2>
                <p className="text-xs text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                <div className="text-[10px] text-gray-500">
                  {post.author} &middot; {post.published_at ? new Date(post.published_at).toLocaleDateString('es-ES') : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {['profesional', 'premium'].includes(user?.subscription_plan) ? (
          <div>
            <h2 className="text-xl font-bold mb-5" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>
              Recursos Profesional
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="rounded-2xl p-6" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.2) 0%, rgba(201,168,76,0.08) 100%)', border: '1px solid rgba(201,168,76,0.3)' }}>
                <FileText size={24} className="text-white mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Formulario de Consejos</h3>
                <p className="text-sm text-white/80 mb-4">Responde 10 preguntas sobre tu situación y recibe consejos personalizados.</p>
                <button 
                  onClick={() => setShowConsultingForm(true)}
                  className="bg-white/20 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-white/30 transition-all" 
                  data-testid="advice-form-btn"
                >
                  Comenzar Ahora
                </button>
              </div>
              <div className={`bg-gradient-to-br ${currentTheme.gradient} rounded-2xl p-6`}>
                <Play size={24} className="text-white mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">Videos Formativos</h3>
                <p className="text-sm text-white/80 mb-4">Biblioteca de videos exclusivos sobre compra, venta e inversion.</p>
                <button className="bg-white/20 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-white/30 transition-all" data-testid="videos-btn">
                  Ver Videos
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Crown size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Desbloquea Contenido Profesional</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Con el plan Profesional obtienes formularios de consejos personalizados, videos formativos exclusivos y mucho mas.
                </p>
                <button
                  onClick={() => navigate('/select-plan')}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-sm font-medium px-5 py-2.5 rounded-lg hover:opacity-90 transition-all"
                >
                  Ver Planes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de Formulario de Consejos */}
      {showConsultingForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gray-900 border-2 border-purple-500 rounded-2xl p-8 max-w-2xl w-full my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Perfil Inversor - Pregunta {consultingStep}/10</h2>
              <button onClick={() => {setShowConsultingForm(false); setConsultingStep(1);}} className="text-gray-400 hover:text-white text-2xl">
                ×
              </button>
            </div>
            
            {consultingStep === 1 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">1. ¿Cuál es tu presupuesto para invertir?</label>
                <select 
                  value={consultingData.budget}
                  onChange={(e) => setConsultingData({...consultingData, budget: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="less_100k">Menos de 100,000 €</option>
                  <option value="100k_250k">100,000 - 250,000 €</option>
                  <option value="250k_500k">250,000 - 500,000 €</option>
                  <option value="500k_plus">Más de 500,000 €</option>
                </select>
              </div>
            )}
            
            {consultingStep === 2 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">2. ¿Cuál es tu objetivo principal?</label>
                <select 
                  value={consultingData.purpose}
                  onChange={(e) => setConsultingData({...consultingData, purpose: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="live">Vivir en la propiedad</option>
                  <option value="rent">Alquilar para ingresos</option>
                  <option value="flip">Comprar y revender</option>
                  <option value="long_term">Inversión a largo plazo</option>
                </select>
              </div>
            )}
            
            {consultingStep === 3 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">3. ¿Qué ubicación prefieres?</label>
                <input 
                  type="text"
                  value={consultingData.location_preference}
                  onChange={(e) => setConsultingData({...consultingData, location_preference: e.target.value})}
                  placeholder="Ej: Madrid, Barcelona, Costa del Sol..."
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                />
              </div>
            )}
            
            {consultingStep === 4 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">4. ¿Qué tipo de propiedad te interesa?</label>
                <select 
                  value={consultingData.property_type_preference}
                  onChange={(e) => setConsultingData({...consultingData, property_type_preference: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="apartamento">Apartamento</option>
                  <option value="casa">Casa/Chalet</option>
                  <option value="local">Local comercial</option>
                  <option value="terreno">Terreno</option>
                </select>
              </div>
            )}
            
            {consultingStep === 5 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">5. ¿En qué plazo quieres comprar?</label>
                <select 
                  value={consultingData.timeline}
                  onChange={(e) => setConsultingData({...consultingData, timeline: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="immediate">Inmediato (0-3 meses)</option>
                  <option value="short">Corto plazo (3-6 meses)</option>
                  <option value="medium">Medio plazo (6-12 meses)</option>
                  <option value="long">Largo plazo (+12 meses)</option>
                </select>
              </div>
            )}
            
            {consultingStep === 6 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">6. ¿Tienes experiencia previa en inversiones inmobiliarias?</label>
                <select 
                  value={consultingData.investment_experience}
                  onChange={(e) => setConsultingData({...consultingData, investment_experience: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="none">Ninguna</option>
                  <option value="beginner">Principiante (1-2 propiedades)</option>
                  <option value="intermediate">Intermedio (3-5 propiedades)</option>
                  <option value="expert">Experto (5+ propiedades)</option>
                </select>
              </div>
            )}
            
            {consultingStep === 7 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">7. ¿Cuál es tu tolerancia al riesgo?</label>
                <select 
                  value={consultingData.risk_tolerance}
                  onChange={(e) => setConsultingData({...consultingData, risk_tolerance: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="low">Baja (prefiero seguridad)</option>
                  <option value="medium">Media (equilibrio riesgo-beneficio)</option>
                  <option value="high">Alta (busco altos rendimientos)</option>
                </select>
              </div>
            )}
            
            {consultingStep === 8 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">8. ¿Necesitas financiación?</label>
                <select 
                  value={consultingData.financing_needed}
                  onChange={(e) => setConsultingData({...consultingData, financing_needed: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="no">No, pago al contado</option>
                  <option value="yes_partial">Sí, financiación parcial</option>
                  <option value="yes_full">Sí, hipoteca completa</option>
                </select>
              </div>
            )}
            
            {consultingStep === 9 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">9. ¿Qué retorno esperas de tu inversión?</label>
                <select 
                  value={consultingData.expected_return}
                  onChange={(e) => setConsultingData({...consultingData, expected_return: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                >
                  <option value="">Selecciona...</option>
                  <option value="3_5">3-5% anual</option>
                  <option value="5_8">5-8% anual</option>
                  <option value="8_12">8-12% anual</option>
                  <option value="12_plus">Más del 12% anual</option>
                </select>
              </div>
            )}
            
            {consultingStep === 10 && (
              <div className="space-y-4">
                <label className="text-white font-semibold block">10. ¿Algún requisito adicional?</label>
                <textarea 
                  value={consultingData.additional_requirements}
                  onChange={(e) => setConsultingData({...consultingData, additional_requirements: e.target.value})}
                  placeholder="Ej: Cerca del mar, con garaje, zona tranquila..."
                  rows={4}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white"
                />
              </div>
            )}
            
            <div className="flex gap-3 mt-6">
              {consultingStep > 1 && (
                <button 
                  onClick={() => setConsultingStep(consultingStep - 1)}
                  className="px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600"
                >
                  Anterior
                </button>
              )}
              {consultingStep < 10 ? (
                <button 
                  onClick={() => setConsultingStep(consultingStep + 1)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-xl hover:opacity-90"
                >
                  Siguiente
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      toast.loading('Analizando tu perfil inversor...');
                      const response = await axios.post(`${API}/knowledge/investor-profile`, consultingData);
                      toast.dismiss();
                      toast.success('¡Perfil inversor generado!');
                      const profile = response.data;
                      setInvestorProfile(profile);
                      setBotMessages([{
                        role: 'bot',
                        text: profile.ai_message,
                        ts: new Date()
                      }]);
                      setShowConsultingForm(false);
                      setConsultingStep(1);
                      setShowInvestorBot(true);
                    } catch (error) {
                      toast.dismiss();
                      toast.error('Error al generar perfil. Inténtalo de nuevo.');
                      console.error(error);
                    }
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-xl hover:opacity-90"
                >
                  🤖 Conocer mi Perfil Inversor
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Bot Socio Inversor */}
      {showInvestorBot && investorProfile && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border-2 border-purple-500 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className={`p-5 rounded-t-2xl flex items-center justify-between ${
              investorProfile.profile === 'rojo' ? 'bg-red-900/60' :
              investorProfile.profile === 'amarillo' ? 'bg-yellow-900/60' :
              'bg-green-900/60'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`text-4xl`}>{investorProfile.profile_emoji}</div>
                <div>
                  <div className="text-xs text-white/60 font-medium">Tu Socio Inversor · Perfil</div>
                  <div className="text-xl font-black text-white">{investorProfile.profile_name}</div>
                </div>
              </div>
              <button onClick={() => setShowInvestorBot(false)} className="text-white/60 hover:text-white p-2">
                <X size={22} />
              </button>
            </div>

            {/* Profile Info */}
            <div className="p-5 border-b border-white/10">
              <p className="text-gray-300 text-sm mb-3">{investorProfile.description}</p>
              <div className="grid grid-cols-1 gap-1.5">
                {investorProfile.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-green-400 font-bold flex-shrink-0">✓</span>
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Chat */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {botMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'bot' && (
                    <div className={`mr-2 mt-1 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      investorProfile.profile === 'rojo' ? 'bg-red-600' :
                      investorProfile.profile === 'amarillo' ? 'bg-yellow-600' : 'bg-green-600'
                    }`}>
                      <Bot size={16} className="text-white" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    msg.role === 'user'
                      ? 'text-black rounded-br-sm'
                      : 'bg-gray-800 text-gray-200 rounded-bl-sm'
                  }`}
                  style={msg.role === 'user' ? { background: 'linear-gradient(135deg, #c9a84c, #e8c870)' } : {}}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {loadingBot && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3 text-gray-400 text-sm animate-pulse">
                    Escribiendo...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={botInput}
                  onChange={(e) => setBotInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && botInput.trim()) {
                      e.preventDefault();
                      const msg = botInput.trim();
                      setBotInput('');
                      setBotMessages(prev => [...prev, { role: 'user', text: msg }]);
                      setLoadingBot(true);
                      axios.post(`${API}/knowledge/investor-chat`, {
                        message: msg,
                        profile: investorProfile.profile,
                        context: consultingData
                      }).then(res => {
                        setBotMessages(prev => [...prev, { role: 'bot', text: res.data.response }]);
                        setLoadingBot(false);
                        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                      }).catch(() => {
                        setLoadingBot(false);
                        toast.error('Error al conectar con el bot');
                      });
                      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                    }
                  }}
                  placeholder="Pregunta a tu socio inversor..."
                  className="flex-1 bg-gray-800 text-white px-4 py-2.5 rounded-xl border border-white/20 text-sm focus:outline-none focus:border-purple-500"
                  disabled={loadingBot}
                />
                <button
                  onClick={async () => {
                    if (!botInput.trim() || loadingBot) return;
                    const msg = botInput.trim();
                    setBotInput('');
                    setBotMessages(prev => [...prev, { role: 'user', text: msg }]);
                    setLoadingBot(true);
                    try {
                      const res = await axios.post(`${API}/knowledge/investor-chat`, {
                        message: msg,
                        profile: investorProfile.profile,
                        context: consultingData
                      });
                      setBotMessages(prev => [...prev, { role: 'bot', text: res.data.response }]);
                      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                    } catch {
                      toast.error('Error al conectar con el bot');
                    }
                    setLoadingBot(false);
                  }}
                  disabled={!botInput.trim() || loadingBot}
                  className="text-black p-2.5 rounded-xl transition disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
